import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  DollarSign, CheckCircle2, Clock, User, MessageSquare, Trophy,
  Shield, Loader2, AlertCircle, Zap
} from "lucide-react";

export default function BidsReceivedCard({ job, onBidAccepted }) {
  const [bids, setBids] = useState([]);
  const [jobInfo, setJobInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acceptingBid, setAcceptingBid] = useState(null);

  useEffect(() => {
    fetchBids();
  }, [job?.id]);

  const fetchBids = async () => {
    if (!job?.id) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/jobs/${job.id}/bids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBids(response.data.bids || []);
      setJobInfo(response.data.job || {});
    } catch (error) {
      console.error("Failed to fetch bids:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    if (!window.confirm("Are you sure you want to accept this bid? Other bids will be rejected.")) {
      return;
    }

    setAcceptingBid(bidId);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/bids/${bidId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bid accepted! The professional has been notified.");
      fetchBids();
      if (onBidAccepted) onBidAccepted();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept bid");
    } finally {
      setAcceptingBid(null);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#1E40AF]" />
        </CardContent>
      </Card>
    );
  }

  const acceptedBid = bids.find(b => b.status === "accepted");
  const hasAcceptedBid = !!acceptedBid;

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#1E40AF]" />
            Bids Received
          </div>
          <Badge variant="outline" className="text-[#1E40AF]">
            {bids.length} bid{bids.length !== 1 ? "s" : ""}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Pricing Info */}
        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
          {jobInfo?.pricing_type === "target_price" ? (
            <p className="text-sm">
              🎯 <span className="text-gray-600">Your target price:</span>{" "}
              <span className="font-bold text-green-600">${jobInfo.target_price}</span>
            </p>
          ) : (
            <p className="text-sm text-gray-600">💬 You requested quotes from professionals</p>
          )}
          {jobInfo?.urgency === "emergency" && (
            <p className="text-sm text-red-600 mt-1">
              🚨 Emergency job — Limited to {jobInfo.max_bids || 3} bids
            </p>
          )}
        </div>

        {/* Winner Banner */}
        {hasAcceptedBid && (
          <div className="mb-4 p-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-5 h-5" />
              <span className="font-bold">Winner Selected!</span>
            </div>
            <p className="text-sm opacity-90">
              You accepted {acceptedBid.business_name || acceptedBid.professional_name}&apos;s bid of ${acceptedBid.bid_amount}
            </p>
          </div>
        )}

        {/* Bids List */}
        {bids.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No bids yet. Professionals are reviewing your job.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bids.map((bid) => (
              <div
                key={bid.id}
                className={`p-4 rounded-xl border-2 transition-all ${
                  bid.status === "accepted"
                    ? "border-green-400 bg-green-50"
                    : bid.status === "rejected"
                    ? "border-gray-200 bg-gray-50 opacity-60"
                    : "border-slate-200 hover:border-[#1E40AF]/50"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Pro Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-semibold text-gray-900">
                        {bid.business_name || bid.professional_name}
                      </span>
                      {bid.is_verified && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                          <Shield className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                      {bid.status === "accepted" && (
                        <Badge className="bg-green-500 text-white text-xs">
                          <Trophy className="w-3 h-3 mr-1" />
                          Winner
                        </Badge>
                      )}
                    </div>
                    {bid.message && (
                      <div className="flex items-start gap-2 mt-2 p-2 bg-white rounded-lg">
                        <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                        <p className="text-sm text-gray-600">{bid.message}</p>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      {new Date(bid.created_at).toLocaleDateString()} at{" "}
                      {new Date(bid.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>

                  {/* Bid Amount & Action */}
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#1E40AF]">${bid.bid_amount}</div>
                    {bid.status === "pending" && !hasAcceptedBid && (
                      <Button
                        size="sm"
                        className="mt-2 bg-green-600 hover:bg-green-700"
                        onClick={() => handleAcceptBid(bid.id)}
                        disabled={acceptingBid === bid.id}
                        data-testid={`accept-bid-${bid.id}`}
                      >
                        {acceptingBid === bid.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Accept
                          </>
                        )}
                      </Button>
                    )}
                    {bid.status === "rejected" && (
                      <Badge variant="outline" className="mt-2 text-gray-500">
                        Not Selected
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
